import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Alert, Card, CardContent,
  Pagination, TextField, InputAdornment, Fade, Button, Chip
} from '@mui/material';
import { DollarSign, ShoppingCart, Coins, TrendingUp, Search, CalendarDays, X } from 'lucide-react';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const StatCard = styled(Card)(() => ({
  background: '#141926',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 16,
}));

const StyledTable = styled(TableContainer)(() => ({
  background: '#141926',
  borderRadius: 16,
  border: '1px solid rgba(255,255,255,0.06)',
  overflowX: 'auto',
  '& .MuiTableCell-root': {
    borderBottom: '1px solid rgba(124, 58, 237, 0.1)',
    color: '#fff',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    color: '#7C3AED',
    fontWeight: 600,
  }
}));

const ITEMS_PER_PAGE = 15;

const AdminVendas = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [vendas, setVendas] = useState([]);
  const [totalVendas, setTotalVendas] = useState(0);
  const [totalTokens, setTotalTokens] = useState(0);
  const [totalFaturamento, setTotalFaturamento] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    fetchVendas();
  }, []);

  const fetchVendas = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/vendas`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.data.success) {
        setVendas(response.data.vendas);
        setTotalVendas(response.data.totalVendas);
        setTotalTokens(response.data.totalTokensVendidos);
        setTotalFaturamento(response.data.totalFaturamento || 0);
      } else {
        setError('Erro ao buscar vendas');
      }
    } catch (err) {
      setError('Erro ao buscar vendas');
    } finally {
      setLoading(false);
    }
  };

  const filteredVendas = useMemo(() => {
    let result = vendas;

    if (dateFrom) {
      const from = new Date(dateFrom + 'T00:00:00');
      result = result.filter(v => v.paidAt && new Date(v.paidAt) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo + 'T23:59:59');
      result = result.filter(v => v.paidAt && new Date(v.paidAt) <= to);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(v =>
        (v.userName || '').toLowerCase().includes(q) ||
        (v.userEmail || '').toLowerCase().includes(q) ||
        (v.order_id || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [vendas, search, dateFrom, dateTo]);

  const filteredFaturamento = useMemo(() => filteredVendas.reduce((acc, v) => acc + (v.valor || 0), 0), [filteredVendas]);
  const filteredTokens = useMemo(() => filteredVendas.reduce((acc, v) => acc + (v.quantidadeTokens || 0), 0), [filteredVendas]);

  const totalPages = Math.ceil(filteredVendas.length / ITEMS_PER_PAGE);
  const paginatedVendas = filteredVendas.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const hasDateFilter = dateFrom || dateTo;

  const monthlyData = useMemo(() => {
    const months = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        label: d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }),
        vendas: 0,
        faturamento: 0,
      });
    }
    vendas.forEach(v => {
      if (!v.paidAt) return;
      const d = new Date(v.paidAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const month = months.find(m => m.key === key);
      if (month) {
        month.vendas++;
        month.faturamento += v.valor || 0;
      }
    });
    return months;
  }, [vendas]);

  const maxFaturamento = Math.max(...monthlyData.map(m => m.faturamento), 1);

  const vendasEsteMes = useMemo(() => {
    const now = new Date();
    const key = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return monthlyData.find(m => m.key === key)?.vendas || 0;
  }, [monthlyData]);

  const faturamentoEsteMes = useMemo(() => {
    const now = new Date();
    const key = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return monthlyData.find(m => m.key === key)?.faturamento || 0;
  }, [monthlyData]);

  const vendasHoje = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return vendas.filter(v => v.paidAt && new Date(v.paidAt).toISOString().split('T')[0] === today);
  }, [vendas]);

  const faturamentoHoje = useMemo(() => vendasHoje.reduce((acc, v) => acc + (v.valor || 0), 0), [vendasHoje]);

  const stats = [
    { label: 'Vendas Hoje', value: vendasHoje.length, sub: `R$ ${faturamentoHoje.toLocaleString('pt-BR')}`, icon: <CalendarDays size={20} strokeWidth={1.75} />, color: '#EF4444' },
    { label: 'Total de Vendas', value: totalVendas, icon: <ShoppingCart size={20} strokeWidth={1.75} />, color: '#7C3AED' },
    { label: 'Tokens Vendidos', value: totalTokens, icon: <Coins size={20} strokeWidth={1.75} />, color: '#10B981' },
    { label: 'Faturamento Total', value: `R$ ${totalFaturamento.toLocaleString('pt-BR')}`, icon: <DollarSign size={20} strokeWidth={1.75} />, color: '#F59E0B' },
    { label: 'Vendas este mês', value: vendasEsteMes, icon: <TrendingUp size={20} strokeWidth={1.75} />, color: '#3B82F6' },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress sx={{ color: '#7C3AED' }} />
      </Box>
    );
  }

  return (
    <Fade in timeout={400}>
      <Box sx={{ p: { xs: 1, sm: 3 } }}>
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <DollarSign size={36} strokeWidth={1.75} color="#F59E0B" /> Vendas
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Stat Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2, mb: 4 }}>
          {stats.map((s, i) => (
            <StatCard key={i}>
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: `${s.color}20`, color: s.color, flexShrink: 0,
                  }}>
                    {s.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.72rem', fontWeight: 500 }}>{s.label}</Typography>
                    <Typography sx={{ color: '#fff', fontSize: '1.2rem', fontWeight: 700 }}>{s.value}</Typography>
                    {s.sub && <Typography sx={{ color: s.color, fontSize: '0.7rem', fontWeight: 600 }}>{s.sub}</Typography>}
                  </Box>
                </Box>
              </CardContent>
            </StatCard>
          ))}
        </Box>

        {/* Gráfico mensal */}
        <Paper sx={{
          p: 3, mb: 4, background: '#141926',
          border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4
        }}>
          <Typography variant="h6" sx={{ color: '#fff', mb: 3 }}>Faturamento Mensal</Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: { xs: 1, sm: 2 }, height: 180 }}>
            {monthlyData.map((m, i) => {
              const heightPct = maxFaturamento > 0 ? (m.faturamento / maxFaturamento) * 100 : 0;
              return (
                <Box key={i} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ color: '#A78BFA', fontSize: { xs: '0.65rem', sm: '0.8rem' }, fontWeight: 600 }}>
                    {m.faturamento > 0 ? `R$${m.faturamento}` : ''}
                  </Typography>
                  <Box sx={{
                    width: '100%', maxWidth: 48, borderRadius: '8px 8px 0 0',
                    minHeight: 4,
                    height: `${Math.max(heightPct, 3)}%`,
                    background: m.faturamento > 0 ? '#7C3AED' : 'rgba(124,58,237,0.1)',
                    transition: 'height 0.6s ease',
                  }} />
                  <Typography sx={{ color: '#6B7280', fontSize: { xs: '0.6rem', sm: '0.75rem' }, textTransform: 'capitalize' }}>
                    {m.label}
                  </Typography>
                  <Typography sx={{ color: '#9CA3AF', fontSize: '0.65rem' }}>
                    {m.vendas} {m.vendas === 1 ? 'venda' : 'vendas'}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Typography sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>
              Este mês: <strong style={{ color: '#F59E0B' }}>R$ {faturamentoEsteMes.toLocaleString('pt-BR')}</strong>
            </Typography>
          </Box>
        </Paper>

        {/* Tabela de vendas */}
        <Paper sx={{
          p: 3, background: '#141926',
          border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" sx={{ color: '#fff' }}>Histórico de Vendas</Typography>
            <TextField
              size="small" placeholder="Buscar por nome, email ou ID"
              value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
              sx={{ width: '100%', maxWidth: 280 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search size={18} strokeWidth={1.75} color="#6B7280" /></InputAdornment> }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <CalendarDays size={16} color="#6B7280" style={{ flexShrink: 0 }} />
            <TextField
              type="date" size="small" label="De"
              value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160, '& .MuiInputBase-input': { fontSize: '0.82rem' } }}
            />
            <TextField
              type="date" size="small" label="Até"
              value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160, '& .MuiInputBase-input': { fontSize: '0.82rem' } }}
            />
            {hasDateFilter && (
              <Button size="small" startIcon={<X size={14} />} onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }}
                sx={{ color: '#9CA3AF', textTransform: 'none', fontSize: '0.78rem', '&:hover': { color: '#fff' } }}>
                Limpar
              </Button>
            )}
            {hasDateFilter && (
              <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto', flexWrap: 'wrap' }}>
                <Chip size="small" label={`${filteredVendas.length} venda${filteredVendas.length !== 1 ? 's' : ''}`}
                  sx={{ background: 'rgba(124,58,237,0.15)', color: '#A78BFA', fontWeight: 600, fontSize: '0.75rem' }} />
                <Chip size="small" label={`${filteredTokens} tokens`}
                  sx={{ background: 'rgba(16,185,129,0.15)', color: '#10B981', fontWeight: 600, fontSize: '0.75rem' }} />
                <Chip size="small" label={`R$ ${filteredFaturamento.toLocaleString('pt-BR')}`}
                  sx={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B', fontWeight: 600, fontSize: '0.75rem' }} />
              </Box>
            )}
          </Box>
          <StyledTable>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Tokens</TableCell>
                  <TableCell>Valor</TableCell>
                  <TableCell>Data</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedVendas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', color: '#6B7280', py: 4 }}>
                      {search ? 'Nenhuma venda encontrada para esta busca.' : 'Nenhuma venda registrada.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedVendas.map((venda) => (
                    <TableRow key={venda.order_id} sx={{ '&:hover': { background: 'rgba(124,58,237,0.05)' } }}>
                      <TableCell sx={{ fontWeight: 500 }}>{venda.userName}</TableCell>
                      <TableCell sx={{ color: '#9CA3AF' }}>{venda.userEmail}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.3, borderRadius: 2, background: 'rgba(16,185,129,0.1)', color: '#10B981', fontWeight: 600, fontSize: '0.85rem' }}>
                          {venda.quantidadeTokens}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: '#F59E0B', fontWeight: 600 }}>
                        R$ {(venda.valor || 0).toLocaleString('pt-BR')}
                      </TableCell>
                      <TableCell sx={{ color: '#9CA3AF' }}>
                        {venda.paidAt ? new Date(venda.paidAt).toLocaleString('pt-BR') : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </StyledTable>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} shape="rounded" />
            </Box>
          )}
        </Paper>
      </Box>
    </Fade>
  );
};

export default AdminVendas;
